import Constants from 'common/constants'

import {
  ADMISSION_LOGIN,
  CHECK_INSTANCES,
  LOGIN_USER,
  LOGIN_USER_JWT,
  LOGIN_WITH_GOOGLE,
  UNIVERSAL_LOGIN,
  UNIVERSAL_LOGIN_WITH_GOOGLE,
  USER_ME,
  VERIFY_LOGIN_CREDENTIALS,
} from './ActionTypes/ApiActionTypes'
import {REDUX_SAVE_LOGIN_INFO} from './ActionTypes/ReduxActionTypes'

/**
 *
 * @param {string} username
 * @param {string} password
 * @param {function(response)} onSuccess
 * @param {function(response)} onFailure
 * @returns
 */
export const login = (username, password, onSuccess, onFailure) => {
  return {
    type: LOGIN_USER,
    payload: {
      onSuccess: onSuccess,
      onFailure: onFailure,
      apiUrl: Constants.api_login,
      noAccessToken: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body: {
        username: username,
        password: password,
      },
      reduxActionType: REDUX_SAVE_LOGIN_INFO,
    },
  }
}

/**
 *
 * @param {string} authToken
 * @param {(res) => void} onSuccess
 * @param {(res) => void} onFailure
 * @param {string} [domain] canonical tenant domain from universal-login (e.g. school.ilmversity.net)
 * @returns
 */
export const loginWithJWT = (authToken, onSuccess, onFailure, domain) => {
  const header2 = domain ? {domain} : {}
  return {
    type: LOGIN_USER_JWT,
    payload: {
      onSuccess,
      onFailure,
      apiUrl: Constants.api_login_jwt,
      noAccessToken: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      header2,
      body: {
        authToken,
      },
      reduxActionType: REDUX_SAVE_LOGIN_INFO,
    },
  }
}

/**
 *
 * @param {(res) => void} onSuccess
 * @param {(res) => void} onFailure
 * @returns
 */
export const getLoggedInUserData = (onSuccess, onFailure) => {
  return {
    type: USER_ME,
    payload: {
      onSuccess,
      onFailure,
      apiUrl: Constants.api_user_me,
      getString: '',
      useBaseURL: true,
      requestType: 'GET',
      header: 'application/json',
      reduxActionType: REDUX_SAVE_LOGIN_INFO,
    },
  }
}

/**
 *
 * @param {string} username
 * @param {string} password
 * @param {function(response)} onSuccess
 * @param {function(response)} onFailure
 * @returns
 */
export const loginWithGoogle = (idToken, onSuccess, onFailure) => {
  return {
    type: LOGIN_WITH_GOOGLE,
    payload: {
      onSuccess,
      onFailure,
      apiUrl: 'user/login/google',
      noAccessToken: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body: {idToken},
      reduxActionType: REDUX_SAVE_LOGIN_INFO,
    },
  }
}

/**
 * AI universal login (main site — no domain header).
 * Success: result.redirectUrl. 409: result.instances (multi-school picker).
 */
export const universalLogin = (username, password, onSuccess, onFailure, applicationId) => {
  const body = {username, password}
  if (applicationId != null) {
    body.applicationId = applicationId
  }

  return {
    type: UNIVERSAL_LOGIN,
    payload: {
      onSuccess,
      onFailure,
      apiUrl: Constants.api_universal_login,
      noAccessToken: true,
      omitDomainHeader: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body,
      reduxActionType: '',
    },
  }
}

/**
 * AI universal Google login (main site — no domain header).
 * Success: result.redirectUrl. 409: result.instances (multi-school picker).
 */
export const universalLoginWithGoogle = (idToken, onSuccess, onFailure, applicationId) => {
  const body = {idToken}
  if (applicationId != null) {
    body.applicationId = applicationId
  }

  return {
    type: UNIVERSAL_LOGIN_WITH_GOOGLE,
    payload: {
      onSuccess,
      onFailure,
      apiUrl: Constants.api_universal_login_google,
      noAccessToken: true,
      omitDomainHeader: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body,
      reduxActionType: '',
    },
  }
}

/** Email-only workspace discovery (optional two-step UX). */
export const checkInstances = (username, onSuccess, onFailure) => {
  return {
    type: CHECK_INSTANCES,
    payload: {
      onSuccess,
      onFailure,
      apiUrl: Constants.api_check_instances,
      noAccessToken: true,
      omitDomainHeader: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body: {username},
      reduxActionType: '',
    },
  }
}

export const admissionLogin = (username, password, onSuccess, onFailure) => {
  return {
    type: ADMISSION_LOGIN,
    payload: {
      onSuccess: onSuccess,
      onFailure: onFailure,
      apiUrl: Constants.api_admission_login,
      noAccessToken: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body: {
        username: username,
        password: password,
      },
      reduxActionType: REDUX_SAVE_LOGIN_INFO,
    },
  }
}

// Signup for admission applicants — the API returns the same login dataObject
// as admission-login, so the payload is stored identically. `mobile` is
// optional (the applicant may leave it blank) and is omitted from the body
// entirely when empty, so an older API build simply never sees it; so is
// `applyingFor` (the grade), which a school with no grades never asks for.
export const admissionSignup = (
  firstName,
  lastName,
  email,
  password,
  onSuccess,
  onFailure,
  mobile,
  applyingFor
) => {
  return {
    type: ADMISSION_LOGIN,
    payload: {
      onSuccess: onSuccess,
      onFailure: onFailure,
      apiUrl: Constants.api_admission_signup,
      noAccessToken: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body: {
        firstName,
        lastName,
        email,
        password,
        ...(mobile ? {mobile} : {}),
        ...(applyingFor ? {applyingFor} : {}),
      },
      reduxActionType: REDUX_SAVE_LOGIN_INFO,
    },
  }
}

// The admission "confirm your email" link: verifies the email and signs the
// applicant in. The API returns the same login dataObject as admission-login
// (only on the click that verifies), so the payload is stored identically.
export const admissionVerifyEmailLogin = (token, login, onSuccess, onFailure) => {
  return {
    type: ADMISSION_LOGIN,
    payload: {
      onSuccess: onSuccess,
      onFailure: onFailure,
      apiUrl: Constants.api_admission_verify_email_login,
      noAccessToken: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body: {token, login},
      reduxActionType: REDUX_SAVE_LOGIN_INFO,
    },
  }
}

export const verifyLoginCredentials = (username, password, onSuccess, onFailure) => {
  return {
    type: VERIFY_LOGIN_CREDENTIALS,
    payload: {
      onSuccess: onSuccess,
      onFailure: onFailure,
      apiUrl: 'user/verify-login-credentials',
      noAccessToken: true,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body: {
        username: username,
        password: password,
      },
      reduxActionType: '',
    },
  }
}
